import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * `?activity=<id>` on the Schedule page: which chip to light up on arrival.
 *
 * The mirror of `?focus=activities` on the case page. Clicking an activity in
 * the case's Activities dialog sends the mediator to the board on that month,
 * and this is how the board knows WHICH of the day's chips they clicked —
 * landing on the right month and leaving them to find it again is most of the
 * reason the trip felt pointless.
 *
 * Returns the id to highlight, or null. It clears itself after a few seconds,
 * so the ring is a greeting rather than a state the board gets stuck in.
 *
 * TWO THINGS THIS DELIBERATELY DOES NOT DO
 * ----------------------------------------
 * It does not list its timers in a cleanup. An earlier version of the case-page
 * half did, with `location.search` in its dependencies, and stripping the URL
 * changed `location.search`, which ran the cleanup, which cancelled the scroll
 * that had not happened yet — the feature looked like dead code. A ref guards
 * the single run instead, and the timers are cleared only on unmount.
 *
 * And it keeps `date` when it strips `activity`. The board's own deep link
 * reads `date` to open on the right month; dropping both would be fine today
 * because that effect is ref-guarded, but it would break the moment anyone
 * made it re-read the URL.
 */
export default function useFocusedActivity() {
    const location = useLocation()
    const navigate = useNavigate()
    const [focusId, setFocusId] = useState(null)
    const handled = useRef(false)
    const timers = useRef([])

    useEffect(() => () => timers.current.forEach(clearTimeout), [])

    useEffect(() => {
        if (handled.current) return
        const id = new URLSearchParams(location.search).get('activity')
        if (!id) return
        handled.current = true
        setFocusId(String(id))

        timers.current.push(
            /* After the chip has had its moment, so a refresh or a Back does
               not replay it. */
            setTimeout(() => {
                const params = new URLSearchParams(location.search)
                params.delete('activity')
                const query = params.toString()
                navigate(query ? `${location.pathname}?${query}` : location.pathname, {
                    replace: true,
                })
            }, 700),
            setTimeout(() => setFocusId(null), 3800)
        )
    }, [location.search, location.pathname, navigate])

    return focusId
}
